import { requestDone, transactionDone } from "./_request";
import type { IdbReadModifyWrite, IdbReadOp, IdbWriteOp } from "./_types";

/**
 * 논리 연산 하나를 트랜잭션 하나로 실행한다.
 *
 * Owns:
 * - 묶음의 모든 op 를 readwrite 트랜잭션 하나에서 실행한다. 중간에 실패하면 전체를 되돌린다.
 * - 대량 쓰기를 청크로 나눈다. 메인 스레드를 오래 차지하지 않게 한다.
 *
 * Does not own:
 * - 무엇을 쓸지 결정하는 일(소비처), 오류 분류(`_errors`), 직렬화(`_writeQueue`).
 *
 * 쓰기는 op 배열로만 받는다. 읽기 후 쓰기의 plan 도 동기로 op 배열만 만든다. plan 이
 * Promise 를 반환하면 중단한다. 외부 I/O 를 기다리는 동안 트랜잭션이 먼저 커밋되지 않게 한다.
 *
 * **청크 사이의 await 가 안전한 이유.** 기다리는 대상이 IDB 요청의 success 이벤트이기 때문이다.
 * 그 이벤트를 처리하는 동안 트랜잭션은 다시 활성이다. 이어지는 microtask 는 이벤트 루프가
 * 다음 작업을 실행하기 전에 실행된다. 그래서 트랜잭션 하나를 유지하면서 브라우저에 실행 시간을
 * 넘겨줄 수 있다. 여기에 setTimeout 같은 매크로태스크를 넣으면 이 성질이 깨진다.
 */

/**
 * 한 청크의 크기. 병목은 IO 가 아니라 메인 스레드의 structured clone 이다. 그래서 한 청크의
 * 복제 비용이 한 프레임 예산 안에 들어가는 수를 고른다. 소비처의 실제 레코드 크기로 다시
 * 조정해야 하는 값이다.
 */
export const WRITE_CHUNK_SIZE = 200;

export async function runWriteBundle(
  db: IDBDatabase,
  ops: readonly IdbWriteOp[],
  chunkSize: number = WRITE_CHUNK_SIZE,
): Promise<void> {
  if (ops.length === 0) {
    return;
  }
  const stores = Array.from(new Set(ops.map((op) => op.store)));
  await runTransaction(db, stores, "readwrite", async (transaction) => {
    for (const op of ops) {
      await applyOp(transaction, op, chunkSize);
    }
  });
}

/** 여러 스토어를 같은 커밋 시점으로 읽는다. 바이트와 장부를 다른 세대로 섞지 않는다. */
export async function runReadSnapshot(
  db: IDBDatabase,
  reads: readonly IdbReadOp[],
): Promise<readonly unknown[]> {
  if (reads.length === 0) return [];
  const stores = Array.from(new Set(reads.map((read) => read.store)));
  return runTransaction(db, stores, "readonly", (transaction) => readValues(transaction, reads));
}

/** 조회부터 쓰기까지 같은 잠금을 유지해, 독립 연결의 변경도 다음 판단에 반영한다. */
export async function runReadModifyWrite(db: IDBDatabase, work: IdbReadModifyWrite): Promise<void> {
  const stores = Array.from(new Set([...work.stores, ...work.reads.map((read) => read.store)]));
  await runTransaction(db, stores, "readwrite", async (transaction) => {
    const values = await readValues(transaction, work.reads);
    const ops = work.plan(values);
    if (!Array.isArray(ops)) {
      // 타입 검사를 우회한 async 판정이 거절해도, 그 거절이 외부로 전달되지 않게 처리한다.
      void Promise.resolve(ops).catch(() => undefined);
      throw new TypeError("IndexedDB write plans must return an operation array synchronously.");
    }
    for (const op of ops) {
      await applyOp(transaction, op, WRITE_CHUNK_SIZE);
    }
  });
}

async function readValues(
  transaction: IDBTransaction,
  reads: readonly IdbReadOp[],
): Promise<readonly unknown[]> {
  const values: unknown[] = [];
  for (const read of reads) {
    const store = transaction.objectStore(read.store);
    values.push(await requestDone(read.kind === "get" ? store.get(read.key) : store.getAll()));
  }
  return values;
}

// work 는 커널 내부 함수만 받는다. 트랜잭션과 await 대상을 소비처에 넘기지 않는다.
async function runTransaction<T>(
  db: IDBDatabase,
  stores: string[],
  mode: "readonly" | "readwrite",
  work: (transaction: IDBTransaction) => Promise<T>,
): Promise<T> {
  const transaction = db.transaction(stores, mode, { durability: "relaxed" });
  const done = transactionDone(transaction);
  try {
    const result = await work(transaction);
    await done;
    return result;
  } catch (error) {
    abortQuietly(transaction);
    await done.catch(() => undefined);
    throw error;
  }
}

async function applyOp(
  transaction: IDBTransaction,
  op: IdbWriteOp,
  chunkSize: number,
): Promise<void> {
  const store = transaction.objectStore(op.store);
  switch (op.kind) {
    case "put":
      await inChunks(op.values, chunkSize, (value) => store.put(value));
      return;
    case "delete":
      await inChunks(op.keys, chunkSize, (key) => store.delete(key));
      return;
    case "deleteRange":
      await requestDone(store.delete(op.range));
      return;
    case "clear":
      await requestDone(store.clear());
      return;
  }
}

/**
 * 청크 단위로 요청을 동기 발행하고, 청크마다 마지막 요청의 완료만 기다린다.
 *
 * 요청을 하나씩 기다리면 왕복이 항목 수만큼 쌓인다. 전부 한 번에 발행하면 그동안 메인 스레드가
 * 멈춘다. 이 방식은 두 방식의 중간이다.
 */
async function inChunks<T>(
  items: readonly T[],
  chunkSize: number,
  issue: (item: T) => IDBRequest,
): Promise<void> {
  for (let start = 0; start < items.length; start += chunkSize) {
    const end = Math.min(start + chunkSize, items.length);
    let last: IDBRequest | null = null;
    for (let index = start; index < end; index += 1) {
      last = issue(items[index]);
    }
    if (last !== null) {
      await requestDone(last);
    }
  }
}

function abortQuietly(transaction: IDBTransaction): void {
  try {
    transaction.abort();
  } catch {
    // 이미 커밋되었거나 스스로 중단된 트랜잭션은 InvalidStateError 를 던진다. 목표는 이미 달성되었다.
  }
}
